import { IdentityPerson, Location, UserCore } from './index';

/**
 * Model to hold a UserAccount data
 */
export class UserAccount {
    public id: string = null;
    public identityPersonId: string = null;
    public location: Location;

    private _firstName: string = null;
    set firstName(firstName: string) {
        this._firstName = firstName && firstName.trim();
    }

    get firstName(): string {
        return this._firstName;
    }

    private _lastName: string = null;
    set lastName(lastName: string) {
        this._lastName = lastName && lastName.trim();
    }

    get lastName(): string {
        return this._lastName;
    }

    private _email: string = null;
    set email(email: string) {
        this._email = email && email.trim();
    }

    get email(): string {
        return this._email;
    }

    get displayName(): string {
        const firstLastName: string = this.formatNameFirstLast();
        if (firstLastName) {
            return firstLastName;
        }
        return this.email;
    }

    /**
     * Constructor
     */
    constructor(id: string = null) {
        this.id = id;
        this.location = new Location();
    }

    public fillFromIdentity(floId: number, identityPerson: IdentityPerson): UserAccount {
        this.id = floId.toString();

        if (identityPerson) {
            this.firstName = identityPerson.firstName;
            this.lastName = identityPerson.lastName;
            this.identityPersonId = identityPerson.id;
            this.email = identityPerson.email;

            if (identityPerson.location) {
                this.location = identityPerson.location.clone();
            }
        }

        return this;
    }

    public clone(): UserAccount {
        const clone: UserAccount = new UserAccount();
        clone.id = this.id;
        clone.firstName = this.firstName;
        clone.lastName = this.lastName;
        clone.email = this.email;
        clone.identityPersonId = this.identityPersonId;

        if (this.location) {
            clone.location = this.location.clone();
        }

        return clone;
    }

    public formatNameFirstLast(): string {
        let name: string = this.firstName || '';

        if (this.lastName) {
            name += name ? ' ' : '';
            name += this.lastName;
        }

        return name;
    }

    public formatNameLastFirst(): string {
        let name: string = this.lastName || '';

        if (this.firstName) {
            name += name ? ', ' : '';
            name += this.firstName;
        }

        return name;
    }

    public fillFromUserCore(userCore: UserCore): UserAccount {
        if (userCore) {
            this.id = userCore.id.toString();
            this.firstName = userCore.firstName;
            this.lastName = userCore.lastName;
            this.email = userCore.email;
        }

        return this;
    }

    /**
     * Serialize to a json api format
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'userAccount';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.attributes.firstName = this.firstName;
        json.data.attributes.lastName = this.lastName;
        json.data.attributes.email = this.email;
        json.data.attributes.identityPersonGuid = this.identityPersonId;

        if (this.location) {
            json.data.attributes.location = this.location.serialize();
        }

        return JSON.stringify(json);
    }

    /**
     * Deserialize from json api format
     */
    public deserialize(json: any): UserAccount {
        const data: any = json.data || json;
        const attrs: any = data.attributes;

        this.id = data.id;
        this.firstName = attrs.firstName;
        this.lastName = attrs.lastName;
        this.email = attrs.email;
        this.identityPersonId = attrs.identityPersonId;

        if (attrs.location) {
            this.location = (new Location()).deserialize(attrs.location);
        }

        return this;
    }
}
